import FilterPanel from '@components/FilterPanel/FilterPanel';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button, Form, Input, message, Modal } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useStore } from 'effector-react';

import blockedEmailDomains, { createDomain, getList } from '@stores/blockedEmailDomains';
import useTranslate from '@hooks/useTranslate';
import componentContent from './BlockedEmailDomainsFilters.content.json';
import css from './BlockedEmailDomainsFilters.module.less';

const BlockedEmailDomainsFilters: FunctionComponent = () => {
  const { filters, preloaders } = useStore(blockedEmailDomains);
  const content = useTranslate(componentContent);
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    form.setFieldsValue({ domain: filters.domain });
  }, [filters.domain, form]);

  const onFinish = ({ domain }: { domain?: string }) => {
    getList({
      filters: {
        ...filters,
        domain: domain?.trim() || undefined,
      },
    });
  };

  const onClear = () => {
    form.resetFields();
    getList({ filters: { sort: filters.sort, domain: undefined } });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    createForm.resetFields();
  };

  const onCreate = async ({ domain }: { domain: string }) => {
    await createDomain({ domain });
    message.success(content.created);
    closeModal();
  };

  return (
    <div className={css.root}>
      <Button
        block
        type='primary'
        icon={<PlusOutlined />}
        className={css.addButton}
        onClick={() => setIsModalOpen(true)}
      >
        {content.add}
      </Button>

      <Form
        form={form}
        layout='vertical'
        onFinish={onFinish}
      >
        <FilterPanel
          actions={
            <div className={css.actions}>
              <Button
                type='primary'
                htmlType='submit'
                loading={!!preloaders.getBlockedEmailDomains}
              >
                {content.search}
              </Button>
              <Button
                htmlType='button'
                onClick={onClear}
                disabled={!!preloaders.getBlockedEmailDomains}
              >
                {content.clear}
              </Button>
            </div>
          }
        >
          <Form.Item
            name='domain'
            label={content.domain}
          >
            <Input
              allowClear
              placeholder={content.domainPlaceholder}
            />
          </Form.Item>
        </FilterPanel>
      </Form>

      <Modal
        visible={isModalOpen}
        title={content.addTitle}
        okText={content.add}
        cancelText={content.cancel}
        confirmLoading={!!preloaders.createBlockedEmailDomain}
        onOk={() => createForm.submit()}
        onCancel={closeModal}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout='vertical'
          onFinish={onCreate}
        >
          <Form.Item
            name='domain'
            label={content.domain}
            normalize={(value: string) => value?.trim().toLowerCase()}
            rules={[
              { required: true, message: content.domainRequired },
              {
                pattern: /^@[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?\.[a-z]{2,}$/i,
                message: content.domainInvalid,
              },
            ]}
          >
            <Input
              autoFocus
              placeholder='@example.com'
              onPressEnter={() => createForm.submit()}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BlockedEmailDomainsFilters;
